<template>
    <div class="dashboard">

        <v-row v-if="loading" no-gutters>
            <v-col class="stats-item" cols="12" sm="4">
                <v-skeleton-loader
                    transition-group="scale-transition"
                    type="article"
                />
            </v-col>
            <v-col class="stats-item" cols="12" sm="4">
                <v-skeleton-loader
                    transition-group="scale-transition"
                    type="article"
                />
            </v-col>
            <v-col class="stats-item" cols="12" sm="4">
                <v-skeleton-loader
                    transition-group="scale-transition"
                    type="article"
                />
            </v-col>

            <v-col class="stats-item" cols="12">
                <v-skeleton-loader
                    transition-group="scale-transition"
                    type="card-heading, image, list-item"
                />
            </v-col>
        </v-row>

        <template v-else>

            <v-row no-gutters>
                <v-col v-if="stats" class="stats-item" cols="12" sm="4">
                    <v-card class="stats-card" outlined>
                        <v-card-title>
                            <div>
                                <div class="heading text-subtitle-1">{{ $t('dashboard.total_sales') }}</div>
                                <span class="text-h4">{{ stats.current.sales.toFormat('0.00') }}</span>
                                <span> {{ Money.currencySymbol }}</span>
                            </div>
                            <v-spacer />
                            <div class="stats-icon primary">
                                <v-icon size="32" color="white">fal fa-coins</v-icon>
                            </div>
                        </v-card-title>

                        <v-card-text v-if="stats.trends.sales != null && stats.trends.sales != -100">
                            <v-divider class="mb-3" />
                            <span
                                :class="{
                                    'success--text': stats.trends.sales >= 0,
                                    'error--text': stats.trends.sales < 0
                                }"
                            >
                                <v-icon v-if="stats.trends.sales >= 0" size="18" color="success" class="mb-1">
                                    fal fa-arrow-alt-up
                                </v-icon>
                                <v-icon v-else size="18" color="error" class="mb-1">
                                    fal fa-arrow-alt-down
                                </v-icon>
                                {{ Math.abs(stats.trends.sales) }}%
                            </span>
                            {{ $t('dashboard.from_last_day') }}
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col v-if="stats" class="stats-item" cols="12" sm="4">
                    <v-card class="stats-card" outlined>
                        <v-card-title>
                            <div>
                                <div class="heading text-subtitle-1">{{ $t('dashboard.receipts') }}</div>
                                <div class="text-h4">{{ stats.current.receiptCount }}</div>
                            </div>
                            <v-spacer />
                            <div class="stats-icon warning">
                                <v-icon size="32" color="white">fal fa-receipt</v-icon>
                            </div>
                        </v-card-title>
                        <v-card-text v-if="stats.trends.receiptCount != null && stats.trends.receiptCount != -100">
                            <v-divider class="mb-3" />
                            <span
                                :class="{
                                    'success--text': stats.trends.receiptCount >= 0,
                                    'error--text': stats.trends.receiptCount < 0
                                }"
                            >
                                <v-icon v-if="stats.trends.receiptCount >= 0" size="18" color="success" class="mb-1">
                                    fal fa-arrow-alt-up
                                </v-icon>
                                <v-icon v-else size="18" color="error" class="mb-1">
                                    fal fa-arrow-alt-down
                                </v-icon>
                                {{ Math.abs(stats.trends.receiptCount) }}%
                            </span>
                            {{ $t('dashboard.from_last_day') }}
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col v-if="stats" class="stats-item" cols="12" sm="4">
                    <v-card class="stats-card" outlined>
                        <v-card-title>
                            <div>
                                <div class="heading text-subtitle-1">{{ $t('dashboard.average_receipt') }}</div>
                                <span class="text-h4">{{ stats.current.receiptAverage.toFormat('0.00') }}</span>
                                <span> {{ Money.currencySymbol }}</span>
                            </div>
                            <v-spacer />
                            <div class="stats-icon success">
                                <v-icon size="32" color="white">fal fa-money-bill</v-icon>
                            </div>
                        </v-card-title>
                        <v-card-text v-if="stats.trends.receiptAverage != null && stats.trends.receiptAverage != -100">
                            <v-divider class="mb-3" />
                            <span
                                :class="{
                                    'success--text': stats.trends.receiptAverage >= 0,
                                    'error--text': stats.trends.receiptAverage < 0
                                }"
                            >
                                <v-icon v-if="stats.trends.receiptAverage >= 0" size="18" color="success" class="mb-1">
                                    fal fa-arrow-alt-up
                                </v-icon>
                                <v-icon v-else size="18" color="error" class="mb-1">
                                    fal fa-arrow-alt-down
                                </v-icon>
                                {{ Math.abs(stats.trends.receiptAverage) }}%
                            </span>
                            {{ $t('dashboard.from_last_day') }}
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col v-if="showDailySalesChart" class="stats-item" cols="12" md="8">
                    <v-card class="stats-card" outlined>
                        <v-card-title>
                            {{ $t('dashboard.total_sales') }}
                        </v-card-title>
                        <v-card-text>
                            <!-- Chart -->
                            <line-chart
                                v-show="dailySales.length"
                                :data="dailySales"
                                style="height: 500px"
                            />

                            <div v-if="!dailySales.length" class="text-subtitle-1">
                                {{ $t('dashboard.no_sales_data') }}
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col
                    class="stats-item"
                    :md="!showDailySalesChart ? 12 : 4"
                    cols="12"
                >
                    <v-card class="stats-card" outlined>
                        <v-card-title>
                            {{ $t('dashboard.activities') }}
                        </v-card-title>
                        <v-card-text>
                            <div class="log">
                                <div v-if="!logLines.length">
                                    {{ $t('dashboard.no_data') }}
                                </div>
                                <div v-for="(logLine, index) in logLines" :key="index">
                                    {{ logLine }}
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

        </template>
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import LineChart from '@/views/shared/admin/dashboard/LineChart';

export default {
    components: {
        LineChart
    },

    created() {
        this.loading = true
        loadData('/admin/load/dashboard').then(data => {
            this.onDataLoaded(data)
            this.loading = false
        });
    },

    data() {
        return {
            logLines: null,
            stats: null,
            dailySales: [],
            loading: false,
        };
    },

    computed: {
        todaysDate() {
            return moment().format('MMMM D, YYYY.');
        },

        showDailySalesChart() {
            return false;
        }
    },

    methods: {
        onDataLoaded(data) {
            this.logLines = data.logLines ? data.logLines.map(line => line.split('|')[0]).reverse() : [];

            this.dailySales = data.dailySales.reverse();

            this.stats = {
                current: {
                    sales: Money.parse(data.today.sales),
                    receiptAverage: Money.parse(data.today.receiptAverage),
                    receiptCount: data.today.receiptCount
                },
                previous: {
                    sales: Money.parse(data.yesterday.sales),
                    receiptAverage: Money.parse(data.yesterday.receiptAverage),
                    receiptCount: data.yesterday.receiptCount
                },
                trends: null
            };

            // Calculate difference between cur and previos shift
            this.stats.trends = {
                receiptCount: this.stats.previous.receiptCount > 0
                    ? _.round(100 * (this.stats.current.receiptCount / this.stats.previous.receiptCount - 1), 1)
                    : null,
                receiptAverage: this.stats.previous.receiptAverage.toUnit() > 0
                    ? _.round(100 * (this.stats.current.receiptAverage.toUnit() / this.stats.previous.receiptAverage.toUnit() - 1), 1)
                    : null,
                sales: this.stats.previous.sales.toUnit() > 0
                    ? _.round(100 * (this.stats.current.sales.toUnit() / this.stats.previous.sales.toUnit() - 1), 1)
                    : null
            };
        }
    }
};
</script>

<style lang="scss" scoped>
.dashboard {
    margin: -10px;

    @media screen and (max-width: 600px) {
        margin: -5px;
    }

    .stats-item {
        padding: 10px;

        @media screen and (max-width: 600px) {
            padding: 5px;
        }

        .stats-card {
            height: 100%;
            background: white;

            .heading {
                color: rgba(0, 0, 0, 0.54);
                font-size: 14px;
            }

            .stats-icon {
                width: 60px;
                height: 60px;
                position: absolute;
                right: 15px;
                top: 20px;
                border-radius: 4px;
                display: flex;
                justify-content: center;
            }

            .log {
                padding: 16px;
                background: #f5f6f9;
                border-radius: 4px;
                color: #313131;
                font-size: 14px;
                max-height: 500px;
                overflow-y: auto;
            }
        }
    }
}
</style>
